import React from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import { Application } from '../../../types';

interface Props {
  application: Application;
  isEditing: boolean;
  onUpdate: (field: string, value: any) => void;
}

const PersonalInfoSection: React.FC<Props> = ({ application, isEditing, onUpdate }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <UserIcon className="w-5 h-5 mr-2 text-primary-600" />
        Personal Information
      </h3>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-600">First Name</label>
          {isEditing ? (
            <input
              type="text"
              value={application.firstName}
              onChange={(e) => onUpdate('firstName', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          ) : (
            <p className="text-gray-900">{application.firstName}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600">Last Name</label>
          {isEditing ? (
            <input
              type="text"
              value={application.lastName}
              onChange={(e) => onUpdate('lastName', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          ) : (
            <p className="text-gray-900">{application.lastName}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600">Date of Birth</label>
          {isEditing ? (
            <input
              type="date"
              value={application.dateOfBirth}
              onChange={(e) => onUpdate('dateOfBirth', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          ) : (
            <p className="text-gray-900">{application.dateOfBirth}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600">Gender</label>
          {isEditing ? (
            <select
              value={application.gender}
              onChange={(e) => onUpdate('gender', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          ) : (
            <p className="text-gray-900 capitalize">{application.gender}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600">Blood Group</label>
          {isEditing ? (
            <select
              value={application.bloodGroup}
              onChange={(e) => onUpdate('bloodGroup', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            >
              <option value="">Select Blood Group</option>
              {['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map(bg => (
                <option key={bg} value={bg}>{bg}</option>
              ))}
            </select>
          ) : (
            <p className="text-gray-900">{application.bloodGroup || 'Not provided'}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600">Father's Name</label>
          {isEditing ? (
            <input
              type="text"
              value={application.fatherName}
              onChange={(e) => onUpdate('fatherName', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          ) : (
            <p className="text-gray-900">{application.fatherName}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600">Mother's Name</label>
          {isEditing ? (
            <input
              type="text"
              value={application.motherName}
              onChange={(e) => onUpdate('motherName', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          ) : (
            <p className="text-gray-900">{application.motherName}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600">Caste</label>
          {isEditing ? (
            <select
              value={application.caste || 'General'}
              onChange={(e) => onUpdate('caste', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            >
              <option value="General">General</option>
              <option value="OBC">OBC</option>
              <option value="SC">SC</option>
              <option value="ST">ST</option>
            </select>
          ) : (
            <p className="text-gray-900">{application.caste || 'Not provided'}</p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600">Aadhaar Number</label>
          {isEditing ? (
            <input
              type="text"
              maxLength={12}
              value={application.aadhaarNumber || ''}
              onChange={(e) => onUpdate('aadhaarNumber', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 font-mono"
            />
          ) : (
            <p className="text-gray-900 font-mono">{application.aadhaarNumber || 'Not provided'}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-600">Height (cm)</label>
            {isEditing ? (
              <input
                type="number"
                value={application.heightCm !== undefined && application.heightCm !== null ? application.heightCm : ''}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  onUpdate('heightCm', isNaN(val) ? '' : val);
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            ) : (
              <p className="text-gray-900">{application.heightCm ? `${application.heightCm} cm` : 'Not provided'}</p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Weight (kg)</label>
            {isEditing ? (
              <input
                type="number"
                value={application.weightKg !== undefined && application.weightKg !== null ? application.weightKg : ''}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  onUpdate('weightKg', isNaN(val) ? '' : val);
                }}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            ) : (
              <p className="text-gray-900">{application.weightKg ? `${application.weightKg} kg` : 'Not provided'}</p>
            )}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-600">Sports Category</label>
          {isEditing ? (
            <input
              type="text"
              value={application.sportsCategory || ''}
              onChange={(e) => onUpdate('sportsCategory', e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          ) : (
            <p className="text-gray-900">{application.sportsCategory || 'Not provided'}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoSection;
